import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Alert,
  Button,
  DataTable,
  InputText,
  SectionCard,
  Select,
  type ColumnDef,
  type SelectOption,
} from "@/deps/ui-library";
import {
  useBankAccounts,
  useBankLedgerAccounts,
  useBankReconciliations,
  useCreateBankAccount,
} from "@/api/queries";
import type { BankAccount, BankReconciliation } from "@/api/types";
import { useCan } from "@/app/session";
import { Dialog } from "@/components/Dialog";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { PermittedAction } from "@/components/PermittedAction";
import { ImportStatementDialog } from "@/features/banking/reconciliation/ImportStatementDialog";
import { today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

type Row = BankAccount & {
  reconciliation: BankReconciliation | undefined;
};

/** How an account stands against what's been imported for it. */
function statusOf(reconciliation: BankReconciliation | undefined): string {
  if (!reconciliation) return "";
  if (reconciliation.onStatementOnly.length > 0) return "To reconcile";
  if (reconciliation.statementBalance === null) return "No statement";
  return Number(reconciliation.difference) === 0 ? "Reconciled" : "To reconcile";
}

/** Where the company's money is, what the ledger says is in each place, and whether the bank agrees. */
export function BankAccountsPage() {
  const can = useCan();
  const { data: accounts, isPending, error } = useBankAccounts();
  const seesReconciliation = can("banking.reconciliation.view");
  const asAt = today();
  const reconciliations = useBankReconciliations(
    (accounts ?? []).map((account) => account.id),
    asAt,
    seesReconciliation,
  );
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  const rows: Row[] = (accounts ?? []).map((account, index) => ({
    ...account,
    reconciliation: reconciliations[index]?.data,
  }));
  const toReview = rows.reduce(
    (sum, row) => sum + (row.reconciliation?.onStatementOnly.length ?? 0),
    0,
  );

  const columns: Array<ColumnDef<Row>> = [
    {
      id: "account",
      header: "Account",
      cell: ({ row: { original: row } }) =>
        seesReconciliation ? (
          <Link
            to="/banking/reconciliation"
            search={{ account: row.id }}
            className="text-primary text-sm hover:underline"
          >
            {row.name}
          </Link>
        ) : (
          <span className="text-fg text-sm">{row.name}</span>
        ),
    },
    {
      id: "number",
      header: "Number",
      cell: ({ row: { original: row } }) => <span className="num">{row.number}</span>,
    },
    ...(seesReconciliation
      ? ([
          {
            id: "statement",
            header: "Statement balance",
            meta: { textAlign: "right", cellClassName: "num" },
            cell: ({ row: { original: row } }: { row: { original: Row } }) =>
              row.reconciliation?.statementBalance == null
                ? ""
                : formatAmount(row.reconciliation.statementBalance),
          },
        ] as Array<ColumnDef<Row>>)
      : []),
    {
      id: "book",
      header: "Book balance",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: row } }) => `${row.currency} ${formatAmount(row.balance)}`,
    },
    ...(seesReconciliation
      ? ([
          {
            id: "review",
            header: "To review",
            meta: { textAlign: "right", cellClassName: "num" },
            cell: ({ row: { original: row } }: { row: { original: Row } }) =>
              row.reconciliation?.onStatementOnly.length ?? "",
          },
          {
            id: "status",
            header: "Status",
            cell: ({ row: { original: row } }: { row: { original: Row } }) =>
              statusOf(row.reconciliation),
          },
        ] as Array<ColumnDef<Row>>)
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Bank accounts"
        subtitle={
          seesReconciliation && accounts
            ? `${noun(accounts.length, "account")} · ${noun(toReview, "line")} to review`
            : undefined
        }
        actions={
          <span className="flex flex-wrap gap-2">
            <PermittedAction
              permission="banking.transfers.create"
              variant="secondary"
              onClick={() => setImporting(true)}
            >
              Import statement
            </PermittedAction>
            <PermittedAction permission="accounting.coa.manage" onClick={() => setAdding(true)}>
              New bank account
            </PermittedAction>
          </span>
        }
      />

      <SectionCard title="Bank accounts" flush>
        {error ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(error as Error).message}
          />
        ) : !isPending && rows.length === 0 ? (
          <EmptyState
            icon="Landmark"
            title="No bank accounts yet"
            description="Add one to record receipts and fund transfers."
          />
        ) : (
          <DataTable
            aria-label="Bank accounts"
            size="sm"
            enableSorting={false}
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            loading={isPending}
            pagination={{ mode: "manual", show: false }}
          />
        )}
      </SectionCard>

      {adding ? <NewBankAccount onClose={() => setAdding(false)} /> : null}
      {importing ? <ImportStatementDialog onClose={() => setImporting(false)} /> : null}
    </div>
  );
}

const noun = (count: number, one: string) => `${count} ${one}${count === 1 ? "" : "s"}`;

const NEW_ACCOUNT = "new";

function NewBankAccount({ onClose }: { onClose: () => void }) {
  const { data: ledger } = useBankLedgerAccounts();
  const create = useCreateBankAccount();
  const [picked, setPicked] = useState<string | null>(null);
  const [ledgerCode, setLedgerCode] = useState("");
  const [name, setName] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [branch, setBranch] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [openingDate, setOpeningDate] = useState(today());
  const [openingProblem, setOpeningProblem] = useState<string | null>(null);

  const options: SelectOption[] = [
    ...(ledger ?? []).map((account) => ({
      value: account.id,
      label: `${account.code} ${account.name}`,
    })),
    { value: NEW_ACCOUNT, label: "Add a new account to the chart" },
  ];
  // Each bank account has its own account in the chart. An unused one comes first.
  const inChart = picked ?? ledger?.[0]?.id ?? NEW_ACCOUNT;
  const opening = openingBalance.replaceAll(",", "").trim();
  const openingOk = opening === "" || /^-?\d+(\.\d{1,2})?$/.test(opening);

  if (openingProblem) {
    return (
      <Dialog open onClose={onClose} title="Bank account added" dismissLabel="Done">
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          description={`The account is ready, but its opening balance wasn't posted. ${openingProblem}`}
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="New bank account"
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={
            name.trim() === "" ||
            !openingOk ||
            (opening !== "" && openingDate === "") ||
            create.isPending
          }
          onClick={() =>
            create.mutate(
              {
                ledgerAccountId: inChart === NEW_ACCOUNT ? null : inChart,
                ledgerCode,
                name,
                bankName,
                accountNumber,
                branch,
                openingBalance: opening,
                openingDate,
              },
              {
                onSuccess: ({ openingBalanceProblem }) => {
                  if (openingBalanceProblem) setOpeningProblem(openingBalanceProblem);
                  else onClose();
                },
              },
            )
          }
        >
          {create.isPending ? "Saving" : "Save bank account"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {create.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(create.error as Error).message}
          />
        ) : null}
        <Select
          label="Account in the chart"
          required
          options={options}
          value={inChart}
          showClearButton={false}
          onChange={(details) => {
            const chosen = Array.isArray(details.value) ? details.value[0] : details.value;
            if (chosen) setPicked(String(chosen.value));
          }}
        />
        {inChart === NEW_ACCOUNT ? (
          <InputText
            label="Ledger code · optional"
            placeholder="The next free code"
            value={ledgerCode}
            onChange={(event) => setLedgerCode(event.target.value)}
          />
        ) : null}
        <InputText
          label="Name"
          required
          placeholder="e.g. Payroll account"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <InputText
          label="Bank"
          value={bankName}
          onChange={(event) => setBankName(event.target.value)}
        />
        <InputText
          label="Account number"
          value={accountNumber}
          onChange={(event) => setAccountNumber(event.target.value)}
        />
        <InputText
          label="Branch"
          value={branch}
          onChange={(event) => setBranch(event.target.value)}
        />
        <div className="grid gap-3 md:grid-cols-2">
          <InputText
            label="Opening balance · optional"
            inputMode="decimal"
            placeholder="0.00"
            className="num"
            value={openingBalance}
            onChange={(event) => setOpeningBalance(event.target.value)}
          />
          <InputText
            label="As at"
            type="date"
            value={openingDate}
            onChange={(event) => setOpeningDate(event.target.value)}
          />
        </div>
        {opening !== "" && openingOk ? (
          <p className="text-fg-muted text-xs">
            Posts a journal voucher: this account against opening balance equity.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
